import { useState } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Button } from "@mui/material";
import { ApiRequest } from "GlobalFunctions";
import { FaRegSquare, FaSquareCheck } from "react-icons/fa6";

interface SelectedPayment {
    subscription_id: string;
    subscription_product_id: number;
    subscription_payment_id: number;
}

export default function BillingHistory(props: any) {

    const { payments, subscriptionId, themeColors } = props;
    const [loadingInvoice, setLoadingInvoice] = useState<number | null>(null);
    const [selectedPayments, setSelectedPayments] = useState<SelectedPayment[]>([]);
    const [sendingInvoices, setSendingInvoices] = useState(false);

    const getInvoice = (subscription_id: any, subscription_product_id: any, subscription_payment_id: any) => {
        setLoadingInvoice(subscription_payment_id);
        ApiRequest({
            method: "get",
            url: "/subscription/getInvoice",
            query: { 
                subscription_id,
                subscription_product_id,
                subscription_payment_id
            },
            setResponse: (response: any) => {
                setLoadingInvoice(null);
                if (response.status === "success") {
                    const link = document.createElement("a");
                    link.href = response.fileUrl;
                    link.setAttribute("download", `invoice_${subscription_payment_id}.pdf`);
                    link.setAttribute("target", "_blank");
                    link.style.display = "none";
                    document.body.appendChild(link);
                    link.click();
                    setTimeout(() => {
                        document.body.removeChild(link);
                    }, 100);
                } else {
                    console.error("Failed to fetch invoice");
                }
            },
        });
    };

    const handleSelectAll = () => {
        if (selectedPayments.length < (payments?.length || 0)) {
            // Select all payments
            const allPayments = payments?.map((payment: any) => ({
                subscription_id: subscriptionId,
                subscription_product_id: payment.product_ids?.[0],
                subscription_payment_id: payment.subscription_payment_id
            })) || [];
            setSelectedPayments(allPayments);
        } else {
            setSelectedPayments([]);
        }
    };

    const handleSelectOne = (payment: any) => {
        const paymentObject: SelectedPayment = {
            subscription_id: subscriptionId,
            subscription_product_id: payment.product_ids?.[0],
            subscription_payment_id: payment.subscription_payment_id
        };

        // Check if this payment is already selected
        const isSelected = selectedPayments.some(
            item => item.subscription_id === paymentObject.subscription_id &&
                item.subscription_product_id === paymentObject.subscription_product_id &&
                item.subscription_payment_id === paymentObject.subscription_payment_id
        );

        if (isSelected) {
            // Remove from selection
            setSelectedPayments(selectedPayments.filter(
                item => !(item.subscription_id === paymentObject.subscription_id &&
                    item.subscription_product_id === paymentObject.subscription_product_id &&
                    item.subscription_payment_id === paymentObject.subscription_payment_id)
            ));
        } else {
            // Add to selection
            setSelectedPayments([...selectedPayments, paymentObject]);
        }
    };

    const sendInvoices = () => {
        setSendingInvoices(true);
        ApiRequest({
            method: "post",
            url: "/subscription/sendInvoices",
            body: {
                payments: selectedPayments
            },
            setResponse: (response: any) => {
                setSendingInvoices(false);
                if (response.status === "success") {
                    setSelectedPayments([]);
                    alert("Invoices sent successfully");
                } else {
                    alert(response.message || "Failed to send invoices");
                }
            },
        });
    };

    return (
        <div className="pc-subscription-billing-history">
            <h2>Billing History</h2>
            <TableContainer className="table_1">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <div onClick={handleSelectAll} style={{ cursor: 'pointer' }} className="td_checkbox">
                                    {payments?.length > 0 && selectedPayments.length === payments?.length ? (
                                        <FaSquareCheck size={20} color={themeColors?.primary} />
                                    ) : (
                                        <FaRegSquare size={20} color="#000" />
                                    )}
                                </div>
                            </TableCell>
                            <TableCell>Order</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Total</TableCell>
                            <TableCell>Payment Status</TableCell>
                            <TableCell>Invoice</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {payments?.map((payment: any, i: number) => (
                            <TableRow key={i}>
                                <TableCell className="td_checkbox" padding="checkbox">
                                    <div
                                        onClick={() => handleSelectOne(payment)}
                                        style={{ cursor: 'pointer' }}
                                        className="td_checkbox"
                                    >
                                        {selectedPayments.some(
                                            item => item.subscription_id === subscriptionId &&
                                                item.subscription_product_id === payment.product_ids?.[0] &&
                                                item.subscription_payment_id === payment.subscription_payment_id
                                        ) ? (
                                            <FaSquareCheck size={20} color={themeColors?.primary} />
                                        ) : (
                                            <FaRegSquare size={20} />
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell># {payment.subscription_payment_id}</TableCell>
                                <TableCell>{payment.payment_date}</TableCell>
                                <TableCell>${Number(payment.payment_amount || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                <TableCell>
                                    <Chip label="● Paid" style={{ backgroundColor: "#ADFFB2", color: "#417029", borderRadius: "5px" }} />
                                </TableCell>
                                <TableCell>
                                    <Button 
                                        variant="outlined" 
                                        className="invoiceButton"
                                        onClick={() => getInvoice(
                                            subscriptionId, 
                                            payment.product_ids?.[0], 
                                            payment.subscription_payment_id
                                        )}
                                        disabled={loadingInvoice === payment.subscription_payment_id}
                                        sx={{
                                            borderColor: themeColors?.primary,
                                            color: themeColors?.primary,
                                            '&:hover': {
                                                borderColor: themeColors?.primary,
                                                backgroundColor: 'transparent'
                                            }
                                        }}
                                    >
                                        Invoice
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {(!payments || payments?.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                                    No billing history available
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {selectedPayments.length > 0 && (
                <a
                    onClick={sendingInvoices ? undefined : sendInvoices}
                    className="sendInvoicesLink"
                    style={{
                        cursor: sendingInvoices ? "not-allowed" : "pointer",
                        opacity: sendingInvoices ? 0.7 : 1,
                        color: themeColors?.primary
                    }}
                >
                    {sendingInvoices ? "Sending..." : <>Send Invoices <span style={{ fontFamily: 'Arial, sans-serif' }}>({selectedPayments.length})</span></>}
                </a>
            )}
        </div>
    );
}
